// /src/components/PropertyManagement.js

import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';
import { 
  FaBuilding, 
  FaEdit, 
  FaTrash, 
  FaQuestionCircle, 
  FaQrcode, 
  FaPlus,
  FaChartLine,
  FaUsers,
  FaCalendar,
  FaSpinner,
  FaTimes,
  FaCheck,
  FaExclamationTriangle,
  FaSignOutAlt
} from 'react-icons/fa';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

// Reusable Components
const PropertyStatCard = ({ title, value, icon: Icon, trend, color = "indigo" }) => (
  <Card className="bg-gray-800 border-gray-700 hover:border-gray-600 transition-all duration-300">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-400">{title}</p>
          <h3 className="text-2xl font-bold text-white mt-2">{value}</h3>
          {trend && (
            <p className={`text-sm ${trend >= 0 ? 'text-green-400' : 'text-red-400'} mt-1`}>
              {trend >= 0 ? '+' : ''}{trend}% from last month
            </p>
          )}
        </div>
        <div className={`p-4 bg-${color}-500 bg-opacity-20 rounded-lg`}>
          <Icon className={`h-6 w-6 text-${color}-400`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const PropertyCard = ({ property, onEdit, onDelete, onLeave, onFAQ, onQRCode }) => {
  const { t } = useTranslation();
  const userId = localStorage.getItem('userId');

  // Check if we have both values before comparing
  const isManaged = userId ? property.owner.toString() !== userId : true;

  // If userId is not available, show an error or handle appropriately
  if (!userId) {
    console.error('User ID not found in localStorage');
    // You might want to trigger a re-login here or handle it differently
  }

  return (
    <Card className="bg-gray-800 border-gray-700 hover:border-indigo-500/50 transition-all duration-300">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <FaBuilding className="text-indigo-400" />
              <h3 className="text-lg font-semibold text-white">{property.name}</h3>
              {/* Add a badge to show if property is managed */}
              {isManaged && (
                <span className="px-2 py-1 text-xs font-medium bg-gray-700 text-gray-300 rounded-full">
                  {t('properties.managed')}
                </span>
              )}
            </div>
            <p className="text-gray-400 mt-2">{property.location}</p>
            <div className="flex items-center mt-2 text-gray-500 text-sm">
              <span className="px-2 py-1 bg-gray-700 rounded-full">
                {property.propertyType}
              </span>
            </div>
          </div>
          <div className="flex space-x-2">
            <button 
              onClick={() => onFAQ(property._id)} 
              className="p-2 text-blue-400 hover:text-blue-300 hover:bg-gray-700 rounded-lg transition-colors"
              title={t('sidebar.navigation.faq.title')}
            >
              <FaQuestionCircle size={18} />
            </button>
            <button 
              onClick={() => onQRCode(property._id)} 
              className="p-2 text-green-400 hover:text-green-300 hover:bg-gray-700 rounded-lg transition-colors"
              title={t('sidebar.navigation.qrcodes.title')}
            >
              <FaQrcode size={18} />
            </button>
            {/* Only show edit button if property is owned */}
            {!isManaged && (
              <button 
                onClick={() => onEdit(property)} 
                className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-gray-700 rounded-lg transition-colors"
                title={t('common.actions.edit')}
              >
                <FaEdit size={18} />
              </button>
            )}
            {/* Show different buttons based on ownership */}
            {isManaged ? (
              <button 
                onClick={() => onLeave(property)}
                className="p-2 text-indigo-400 hover:text-indigo-300 hover:bg-gray-700 rounded-lg transition-colors"
                title={t('properties.actions.leave')}
              >
                <FaSignOutAlt size={18} />
              </button>
            ) : (
              <button 
                onClick={() => onDelete(property._id)} 
                className="p-2 text-red-400 hover:text-red-300 hover:bg-gray-700 rounded-lg transition-colors"
                title={t('common.actions.delete')}
              >
                <FaTrash size={18} />
              </button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const PropertyForm = ({ property, onSubmit, onCancel, isLoading }) => (
  <form onSubmit={onSubmit} className="space-y-4">
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-1">
        Property Name
      </label>
      <input
        type="text"
        name="name"
        defaultValue={property?.name}
        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
        required
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-1">
        Property Type
      </label>
      <input
        type="text"
        name="propertyType"
        defaultValue={property?.propertyType}
        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
        required
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-1">
        Location
      </label>
      <input
        type="text"
        name="location"
        defaultValue={property?.location}
        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
        required
      />
    </div>
    <div className="flex justify-end space-x-3 mt-6">
      <button
        type="button"
        onClick={onCancel}
        className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={isLoading}
        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center"
      >
        {isLoading ? (
          <>
            <FaSpinner className="animate-spin mr-2" />
            Saving...
          </>
        ) : (
          <>
            <FaCheck className="mr-2" />
            Save Property
          </>
        )}
      </button>
    </div>
  </form>
);

const ConfirmDialog = ({ message, onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
    <Card className="w-full max-w-md bg-gray-800 border-gray-700">
      <CardContent className="p-6">
        <div className="flex items-center mb-4">
          <FaExclamationTriangle className="text-yellow-400 mr-2 text-xl" />
          <h3 className="text-lg font-semibold text-white">Confirm Action</h3>
        </div>
        <p className="text-gray-300 mb-6">{message}</p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Confirm
          </button>
        </div>
      </CardContent>
    </Card>
  </div>
);

const PropertyManagement = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [ownedProperties, setOwnedProperties] = useState([]);
  const [managedProperties, setManagedProperties] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [error, setError] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await api.get('/property/user/current', {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      setOwnedProperties(response.data.owned);
      setManagedProperties(response.data.managed);
      setProperties([...response.data.owned, ...response.data.managed]);
    } catch (error) {
      console.error('Failed to fetch properties:', error);
      setError('Failed to fetch properties. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const formData = {
      name: e.target.name.value,
      propertyType: e.target.propertyType.value,
      location: e.target.location.value,
    };

    try {
      if (editingProperty) {
        await api.put(
          `/property/${editingProperty._id}`,
          formData,
          { headers: { 'x-auth-token': localStorage.getItem('token') } }
        );
      } else {
        await api.post(
          '/property',
          formData,
          { headers: { 'x-auth-token': localStorage.getItem('token') } }
        );
      }
      fetchProperties();
      setShowForm(false);
      setEditingProperty(null);
    } catch (error) {
      console.error('Failed to save property:', error);
      setError('Failed to save property. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setShowForm(true);
  };

  const handleDelete = (propertyId) => {
    setPropertyToDelete(propertyId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/property/${propertyToDelete}`, {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      fetchProperties();
      setShowDeleteConfirm(false);
      setPropertyToDelete(null);
    } catch (error) {
      console.error('Failed to delete property:', error);
      setError('Failed to delete property. Please try again.');
    }
  };

  const navigateToFAQ = (propertyId) => {
    navigate(`/faqs/${propertyId}`);
  };

  const navigateToQRCode = (propertyId) => {
    navigate(`/qrcodes/${propertyId}`);
  };

  const handleLeaveProperty = async (property) => {
    toast(
      t('properties.actions.leaveConfirm'),
      {
        action: {
          label: t('properties.actions.leave'),
          onClick: async () => {
            try {
              await api.post(
                '/invitation/leave-property', 
                { propertyId: property._id },
                { headers: { 'x-auth-token': localStorage.getItem('token') } }
              );
  
              // Fetch updated properties after leaving
              const response = await api.get('/property/user/current', {
                headers: { 'x-auth-token': localStorage.getItem('token') }
              });
              
              // Update both owned and managed properties
              setOwnedProperties(response.data.owned);
              setManagedProperties(response.data.managed);
              
              toast.success(t('properties.actions.leaveSuccess'));
            } catch (error) {
              console.error('Failed to leave property:', error);
              const errorMessage = error.response?.data?.error || t('properties.actions.leaveFailed');
              toast.error(errorMessage);
            }
          },
        },
        cancel: {
          label: t('common.actions.cancel'),
          onClick: () => toast.dismiss(),
        },
        duration: Infinity,
      }
    );
  };

  return (
    <div className="flex-1 space-y-8 p-8 pt-6">
      {error && (
        <div className="bg-red-500 bg-opacity-10 border border-red-500 text-red-500 px-4 py-3 rounded relative">
          {error}
          <button
            className="absolute top-3 right-3"
            onClick={() => setError(null)}
          >
            <FaTimes />
          </button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold text-white">Properties Overview</h2>
        <button
          onClick={() => {
            setEditingProperty(null);
            setShowForm(true);
          }}
          className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
        >
          <FaPlus className="mr-2" />
          Add Property
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        <PropertyStatCard
          title="Total Properties"
          value={properties.length}
          icon={FaBuilding}
          trend={12}
        />
        <PropertyStatCard
          title="Active Bookings"
          value="24"
          icon={FaCalendar}
          trend={8}
          color="green"
        />
        <PropertyStatCard
          title="Total Revenue"
          value="$45,231"
          icon={FaChartLine}
          trend={23}
          color="blue"
        />
        <PropertyStatCard
          title="Active Guests"
          value="156"
          icon={FaUsers}
          trend={18}
          color="purple"
        />
      </div>

      {/* Properties Lists */}
      {/* Owned Properties */}
      {ownedProperties.length > 0 && (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-white">{t('sidebar.propertySection.owned')}</h3>
          <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
            {ownedProperties.map(property => (
              <PropertyCard
                key={property._id}
                property={property}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onFAQ={navigateToFAQ}
                onQRCode={navigateToQRCode}
                onLeave={handleLeaveProperty}
              />
            ))}
          </div>
        </div>
      )}

      {/* Managed Properties */}
      {managedProperties.length > 0 && (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-white">{t('sidebar.propertySection.managed')}</h3>
          <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
            {managedProperties.map(property => (
              <PropertyCard
                key={property._id}
                property={property}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onFAQ={navigateToFAQ}
                onQRCode={navigateToQRCode}
                onLeave={handleLeaveProperty}
              />
            ))}
          </div>
        </div>
      )}

      {/* Add/Edit Property Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-white">
                {editingProperty ? 'Edit Property' : 'Add New Property'}
              </CardTitle>
            </CardHeader>
            <CardContent>
            <PropertyForm
                property={editingProperty}
                onSubmit={handleSubmit}
                onCancel={() => {
                  setShowForm(false);
                  setEditingProperty(null);
                }}
                isLoading={isLoading}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <ConfirmDialog
          message="Are you sure you want to delete this property? This action cannot be undone."
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteConfirm(false);
            setPropertyToDelete(null);
          }}
        />
      )}

      {/* Empty State */}
      {properties.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <FaBuilding className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No Properties Yet</h3>
          <p className="text-gray-400 mb-4">Get started by adding your first property</p>
          <button
            onClick={() => {
              setEditingProperty(null);
              setShowForm(true);
            }}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
          >
            <FaPlus className="mr-2" />
            Add Property
          </button>
        </div>
      )}

      {/* Loading State */}
      {isLoading && properties.length === 0 && (
        <div className="text-center py-12">
          <FaSpinner className="mx-auto h-8 w-8 text-indigo-500 animate-spin" />
          <p className="text-gray-400 mt-2">Loading properties...</p>
        </div>
      )}
    </div>
  );
};

export default PropertyManagement;